/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  Titulo,
  Boton,
  CampoInformacion,
  Sidebar,
  formatFecha,
  TablaFiltrable
} from "../../shared";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import DatosContacto from "./DatosContacto";

const TAG_CLASIFICACION = {
  CONTROL: { value: "Control", severity: "danger" },
  CROSS_SELL: { value: "Cross-sell", severity: "success" },
  PROSPECCION: { value: "Prospección", severity: "info" }
};

// Acordeón de importaciones (Penta-Transaction) del cliente, en su sección de
// procesos. A diferencia del acordeón "Nuevas importaciones de clientes" del
// home, acá se muestran TODAS las importaciones del cliente, de cualquier
// clasificación, vistas o no: son dos vistas independientes del mismo dato.
// Si el cliente no tiene ninguna, no se muestra nada.
export default function ImportacionesCliente({ clienteId }) {
  const [importaciones, setImportaciones] = useState(null);
  const [seleccionada, setSeleccionada] = useState(null);
  const toast = useRef(null);

  // A diferencia del home, acá la fila no desaparece al marcarla: esta vista
  // muestra vistas y no vistas, solo se apaga el botón.
  function marcarComoVisto(importacionId) {
    accessAPI(
      "PUT",
      `admin/importacion/${importacionId}/visto`,
      null,
      () => {
        setImportaciones((prev) =>
          prev.map((i) => (i.id === importacionId ? { ...i, visto: true } : i))
        );
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al marcar como visto",
          sticky: true
        });
      }
    );
  }

  useEffect(() => {
    if (!clienteId) return;
    accessAPI(
      "GET",
      `admin/importacion/cliente/${clienteId}`,
      null,
      (respuesta) => setImportaciones(respuesta),
      () => setImportaciones([])
    );
  }, [clienteId]);

  if (!importaciones || importaciones.length === 0) return null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Accordion
        transitionOptions={{ timeout: 0 }}
        css={{ marginBottom: "1em", "& .p-accordion-header-link": { color: "black" }, "& .p-toggleable-content": { transition: "none !important" } }}
      >
        <AccordionTab header={`Importaciones realizadas (${importaciones.length})`}>
          <TablaFiltrable conFiltros={false}
            value={importaciones}
            paginator
            rows={10}
            stripedRows
            size="small"
            emptyMessage="Sin importaciones"
            onRowClick={(e) => {
              if (e.originalEvent.target.closest("button")) return;
              setSeleccionada(e.data);
            }}
            rowClassName={() => "cursor-pointer"}
            nombreArchivo="importaciones_cliente"
          >
            <Column
              field="certificadotipo.nombre"
              header="Tipo de producto"
              body={(i) => i.certificadotipo?.nombre || "-"}
            />
            <Column
              header="Cantidad"
              body={(i) => i.cantidad || "-"}
              style={{ width: "10em" }}
              alignHeader="center"
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              header="Fecha de importación"
              body={(i) => formatFecha(i.fechaImportacion)}
              style={{ width: "12em" }}
              alignHeader="center"
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              body={(i) => {
                const tag = TAG_CLASIFICACION[i.clasificacion];
                return tag ? <Tag value={tag.value} severity={tag.severity} /> : i.clasificacion;
              }}
              style={{ width: "10em" }}
            />
            <Column
              body={(i) =>
                i.visto ? (
                  <i className="pi pi-check" css={{ color: "var(--verde)" }} />
                ) : (
                  <Boton callback={() => marcarComoVisto(i.id)} chico texto>
                    Marcar como visto
                  </Boton>
                )
              }
              style={{ width: "12em" }}
            />
          </TablaFiltrable>
        </AccordionTab>
      </Accordion>

      <Sidebar
        visible={!!seleccionada}
        onHide={() => setSeleccionada(null)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        {seleccionada && (
          <div css={{ marginBottom: "50px" }}>
            <Titulo>Importación</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Tipo de producto">
                {seleccionada.certificadotipo?.nombre || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de importación">
                {formatFecha(seleccionada.fechaImportacion)}
              </CampoInformacion>
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="NCM">{seleccionada.ncm}</CampoInformacion>
              <CampoInformacion nombreCampo="RUT">
                {seleccionada.rutImportador || "-"}
              </CampoInformacion>
            </div>
            <div className="grid">
              <CampoInformacion nombreCampo="Teléfono">
                {seleccionada.telefono || "-"}
              </CampoInformacion>
            </div>
            <div className="grid">
              <CampoInformacion nombreCampo="Dirección" anchoCompleto>
                {seleccionada.direccion || "-"}
              </CampoInformacion>
            </div>
            <DatosContacto datosContacto={seleccionada.datosContacto} />
          </div>
        )}
      </Sidebar>
    </>
  );
}
